import { useMemo, useState } from "react";
import { ModalHeader, ModalBody, Button, Spinner } from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faExclamationTriangle,
    faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { SimulatedCropPlan } from "@/types/cropPlan";
import { CropStage } from "@/types/cropConfiguration";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import TableV2 from "../../table/TableV2";
import { RankingCell } from "../../table/RankingCell";
import InfoModal from "../../common/InfoModal";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { roundTo } from "@/lib/utils/numbers";
import { ProcessingStatus } from "@/types/fieldNitrogenPlan";

type TableType = SimulatedCropPlan & {
    penalty_early_CWAD_rank: number;
    penalty_NIAD_rank: number;
    penalty_remob_rank: number;
    penalty_roi_rank: number;
    penalty_early_NUPC_rank: number;
    type: string;
};

function PatientRankingCell({
    value,
    status,
}: {
    value: number;
    status?: ProcessingStatus;
}) {
    if (
        status === ProcessingStatus.PROCESSING ||
        status === ProcessingStatus.PENDING
    ) {
        return <Spinner size="sm" />;
    }
    if (
        status === ProcessingStatus.FAILED ||
        status === ProcessingStatus.PARTIALLY_COMPLETED
    ) {
        return <FontAwesomeIcon color="red" icon={faExclamationTriangle} />;
    }
    return <RankingCell value={value} />;
}

function PlanComparison({
    cropPlans,
    cropStageOptions,
}: {
    cropPlans: SimulatedCropPlan[];
    cropStageOptions: CropStage[];
}) {
    const [sorting, setSorting] = useState<SortingState>([
        { id: "penalty_early_CWAD_rank", desc: false },
    ]);

    const cropStageLookup = useMemo(() => {
        return cropStageOptions.reduce(
            (acc, option) => {
                acc[option.id] = option;
                return acc;
            },
            {} as Record<number, CropStage>,
        );
    }, [cropStageOptions]);

    const modelRateLookup = useMemo(() => {
        return cropPlans.reduce(
            (acc, plan) => {
                plan.simulated_rates?.forEach((rate) => {
                    acc[rate.planned_application] = rate.optimal_rate;
                });
                return acc;
            },
            {} as Record<number, number>,
        );
    }, [cropPlans]);

    const comparisons = useMemo(() => {
        return cropPlans.flatMap((plan) => {
            const hasUserRates =
                plan.application_plan?.planned_applications.some(
                    (application) => application.user_rate !== null,
                );
            const hasAllUserRates =
                plan.application_plan?.planned_applications.every(
                    (application) => application.user_rate !== null,
                );
            const plans = [
                {
                    ...plan,
                    type: "model",
                },
            ];

            if (hasUserRates) {
                plans.push({
                    ...plan,
                    type: hasAllUserRates ? "user" : "combined",
                    penalty_early_CWAD: plan.user_penalty_early_CWAD,
                    penalty_NIAD: plan.user_penalty_NIAD,
                    penalty_remob: plan.user_penalty_remob,
                    penalty_roi: plan.user_penalty_roi,
                    penalty_early_NUPC: plan.user_penalty_early_NUPC,
                });
            }
            return plans;
        });
    }, [cropPlans]);

    const comparisonColumns: ColumnDef<TableType>[] = [
        {
            id: "application_plan.name",
            header: "Plan",
            accessorFn: (row) => row.application_plan?.name,
            cell: ({ row }) => (
                <div className="text-base font-semibold flex-col gap-2">
                    {row.original.application_plan?.name}
                </div>
            ),
            size: 250,
        },
        {
            id: "type",
            header: "Rate Type",
            accessorFn: (row) => row.type,
            cell: ({ row }) => (
                <div>
                    {row.original.type === "model"
                        ? "Model"
                        : row.original.type === "user"
                          ? "User"
                          : "User & Model"}
                </div>
            ),
            size: 150,
        },
        {
            id: "applications",
            header: "Applications",
            accessorFn: (row) => row.application_plan?.planned_applications,
            cell: ({ row }) => {
                const applications = sortArrayByKey(
                    row.original.application_plan?.planned_applications ?? [],
                    (app) =>
                        cropStageLookup[app.target_crop_stage]
                            ?.days_after_planting,
                );

                return (
                    <div className="text-xs">
                        {applications.map((application) => {
                            const rate =
                                row.original.type === "combined"
                                    ? (modelRateLookup?.[application.id] ??
                                      application.user_rate)
                                    : row.original.type == "user"
                                      ? application.user_rate
                                      : modelRateLookup?.[application.id];
                            return (
                                <div
                                    key={application.id}
                                    className="grid grid-cols-2"
                                >
                                    <div>
                                        {
                                            cropStageLookup[
                                                application.target_crop_stage
                                            ]?.growth_stage
                                        }
                                    </div>
                                    <div>{rate} lbs</div>
                                </div>
                            );
                        })}
                    </div>
                );
            },
            size: 250,
        },
        {
            id: "total_nitrogen_applied",
            header: "Total Nitrogen Applied",
            accessorFn: (row) => row.total_nitrogen_applied,
            cell: ({ row }) => {
                const totalN =
                    row.original.application_plan?.planned_applications?.reduce(
                        (acc, application) => {
                            const rate =
                                row.original.type === "combined"
                                    ? (modelRateLookup?.[application.id] ??
                                      application.user_rate)
                                    : row.original.type == "user"
                                      ? application.user_rate
                                      : modelRateLookup?.[application.id];

                            return acc + (rate ?? 0);
                        },
                        0,
                    ) ?? 0;
                return <div>{roundTo(totalN, 0)} lbs</div>;
            },
            size: 150,
        },
        {
            id: "penalty_early_CWAD_rank",
            header: "Early Biomass",
            accessorFn: (row) => row.penalty_early_CWAD_rank,
            cell: ({ row }) => (
                <PatientRankingCell
                    value={row.original.penalty_early_CWAD_rank}
                    status={row.original.processing_status ?? undefined}
                />
            ),
            size: 150,
        },
        {
            id: "penalty_NIAD_rank",
            header: "Residual N",
            accessorFn: (row) => row.penalty_NIAD_rank,
            cell: ({ row }) => (
                <PatientRankingCell
                    value={row.original.penalty_NIAD_rank}
                    status={row.original.processing_status ?? undefined}
                />
            ),
            size: 150,
        },
        {
            id: "penalty_remob_rank",
            header: "Translocation",
            accessorFn: (row) => row.penalty_remob_rank,
            cell: ({ row }) => (
                <PatientRankingCell
                    value={row.original.penalty_remob_rank}
                    status={row.original.processing_status ?? undefined}
                />
            ),
            size: 150,
        },
        {
            id: "penalty_roi_rank",
            header: "Profit Potential",
            accessorFn: (row) => row.penalty_roi_rank,
            cell: ({ row }) => (
                <PatientRankingCell
                    value={row.original.penalty_roi_rank}
                    status={row.original.processing_status ?? undefined}
                />
            ),
            size: 150,
        },
        {
            id: "penalty_early_NUPC_rank",
            header: "N Uptake",
            accessorFn: (row) => row.penalty_early_NUPC_rank,
            cell: ({ row }) => (
                <PatientRankingCell
                    value={row.original.penalty_early_NUPC_rank}
                    status={row.original.processing_status ?? undefined}
                />
            ),
            size: 150,
        },
    ];

    const cropPlanRankings: TableType[] = useMemo(() => {
        const values = {
            penalty_early_CWAD: comparisons.map(
                (plan) => plan.penalty_early_CWAD || Infinity,
            ),
            penalty_NIAD: comparisons.map(
                (plan) => plan.penalty_NIAD || Infinity,
            ),
            penalty_remob: comparisons.map(
                (plan) => plan.penalty_remob || Infinity,
            ),
            penalty_roi: comparisons.map(
                (plan) => plan.penalty_roi || Infinity,
            ),
            penalty_early_NUPC: comparisons.map(
                (plan) => plan.penalty_early_NUPC || Infinity,
            ),
        };
        const rankings: { [key: string]: number[] } = {};
        Object.entries(values).forEach(([key, vals]) => {
            const sorted = [...vals].sort((a, b) => a - b);
            rankings[key] = vals.map((val) => sorted.indexOf(val) + 1);
        });
        return comparisons.map((plan, index) => ({
            ...plan,
            penalty_early_CWAD_rank: rankings["penalty_early_CWAD"][index],
            penalty_NIAD_rank: rankings["penalty_NIAD"][index],
            penalty_remob_rank: rankings["penalty_remob"][index],
            penalty_roi_rank: rankings["penalty_roi"][index],
            penalty_early_NUPC_rank: rankings["penalty_early_NUPC"][index],
        }));
    }, [comparisons]);

    return (
        <>
            <div className="mx-auto my-4">
                <div className="flex flex-row justify-center items-center mb-4">
                    <InfoModal
                        trigger={(setShowModal) => (
                            <div className="flex flex-row items-center w-full gap-2 text-sm">
                                Performance Rankings
                                <Button
                                    onPress={() => setShowModal(true)}
                                    size="sm"
                                    variant="light"
                                    color="primary"
                                    isIconOnly
                                >
                                    <FontAwesomeIcon icon={faInfoCircle} />
                                </Button>
                            </div>
                        )}
                    >
                        <ModalHeader className="font-semibold text-xl">
                            Penalties
                        </ModalHeader>
                        <ModalBody>
                            <p>
                                The penalties are calculated based on the
                                following criteria:
                            </p>
                        </ModalBody>
                    </InfoModal>
                </div>
            </div>
            <TableV2
                data={cropPlanRankings}
                columns={comparisonColumns}
                sorting={sorting}
                setSorting={setSorting}
                manualSorting={false}
                estimateSize={80}
            />
        </>
    );
}

export default PlanComparison;
